import { supabase } from './supabase';
import { SupabaseClient } from '@supabase/supabase-js';
import { parseCustomerAdditionalAddresses } from './customers';
import { ServiceAddon } from './services';
import { createBookingScheduledMilestone } from './bookingMilestones';
import { createBookingWorkflowWithStepsV2 } from './bookingWorkflows';
import { cleanupBookingProducts } from './stockItems';
import { findOpenLinkableOpportunityForCustomer } from './crm/opportunityLinking';
import { recordOpportunityActivity } from './opportunityActivities';

// Assert that supabase is not null
const client = supabase as SupabaseClient;

export function triggerGcalSync(
  entityId: string,
  action: 'create' | 'update' | 'delete',
  businessId: string,
  eventType: 'booking' | 'task' | 'field_quote' = 'booking'
) {
  if (!supabase) return;
  const idKey = eventType === 'task' ? 'taskId' : eventType === 'field_quote' ? 'fieldQuoteId' : 'bookingId';
  supabase.functions.invoke('gcal-sync-push', {
    body: { [idKey]: entityId, action, businessId, eventType },
  }).catch((err: any) => console.warn('Google Calendar sync failed (non-critical):', err));
}

let statusChangeCallback: ((booking: Booking) => void) | null = null;

export function subscribeToBookingStatusChanges(callback: (booking: Booking) => void) {
  console.log('Setting up booking status change subscription with callback:', !!callback);
  statusChangeCallback = callback;
  
  const channel = client
    .channel('booking-status-changes')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'bookings'
      },
      async (payload: any) => {
        console.log('Received database event:', {
          event: payload.eventType,
          old: payload.old,
          new: payload.new,
          table: payload.table,
          schema: payload.schema
        });

        // Only process UPDATE events
        if (payload.eventType !== 'UPDATE') {
          console.log('Ignoring non-UPDATE event:', payload.eventType);
          return;
        }

        // Check if status has changed
        if (payload.old?.status !== payload.new?.status) {
          console.log('Status change detected:', {
            oldStatus: payload.old?.status,
            newStatus: payload.new?.status,
            bookingId: payload.new?.id,
            timestamp: new Date().toISOString()
          });

          try {
            const booking = await getBooking(payload.new.id);
            console.log('Fetched booking details for automation:', {
              id: booking?.id,
              status: booking?.status,
              previousStatus: booking?.previous_status,
              hasCallback: !!statusChangeCallback,
              hasCustomer: !!booking?.customer,
              customerPhone: booking?.customer?.phone,
              timestamp: new Date().toISOString()
            });

            if (booking && statusChangeCallback) {
              console.log('Invoking automation callback');
              statusChangeCallback(booking);
            } else {
              console.log('Skipping automation callback:', {
                hasBooking: !!booking,
                hasCallback: !!statusChangeCallback
              });
            }
          } catch (error) {
            console.error('Error processing booking status change:', error);
          }
        } else {
          console.log('No status change detected:', {
            status: payload.new?.status,
            bookingId: payload.new?.id,
            timestamp: new Date().toISOString()
          });
        }
      }
    )
    .subscribe((status) => {
      console.log('Subscription status:', status);
    });

  return channel;
}

export interface CalendarBooking {
  id: string;
  title: string;
  booking_number: number;
  start: string;
  end: string;
  backgroundColor: string;
  borderColor: string;
  secondaryColor?: string | null;
  allDay?: boolean;
  display?: string;
  resourceId?: string;
  extendedProps: {
    customer: string;
    customer_phone?: string | null;
    customer_email?: string | null;
    customer_id?: string | null;
    status: string;
    booking_number: number;
    subtotal: number;
    total_amount: number;
    discount_amount?: number; // Added for sales calculation
    end_date?: string | null; // Added for sales calculation
    discounts?: BookingDiscount[];
    payments?: Payment[];
    services?: BookingService[];
    resources?: Array<{
      id: string;
      first_name: string;
      last_name: string;
      avatar_url?: string;
    }>;
    is_buffer_block?: boolean;
    buffer_time?: number;
    booking_id?: string;
    resource_id?: string;
    mobile_booking?: boolean;
    customer_notes?: string;
    secondaryColor?: string;
    drop_off_time?: string | null;
    start_time?: string | null;
    booking_address?: string | null;
    booking_workflows?: Array<{
      id: string;
      name?: string;
      service_id?: string;
      steps?: Array<{
        id: string;
        workflow_step_library_id?: string;
        title: string;
        description?: string;
        order_index: number;
        status: string;
        completed_at?: string;
        notes?: string;
        assigned_resource_id?: string;
      }>;
    }>;
    // Customer location for map view
    address?: string | null;
    lat?: number | null;
    lng?: number | null;
    // Non-booking calendar items
    is_time_off?: boolean;
    is_field_quote?: boolean;
    field_quote_id?: string;
    field_quote_status?: string;
    is_task?: boolean;
  };
}

// Helper to ensure valid date/time string
const formatDateTime = (date: string, time: string): string => {
  // Ensure date and time are strings
  const dateStr = String(date);
  const timeStr = String(time);
  // Add seconds if not present
  const timeWithSeconds = timeStr.length === 5 ? `${timeStr}:00` : timeStr;
  // Return ISO string
  return `${dateStr}T${timeWithSeconds}`;
};

// Helper to get exclusive end date for all-day/multi-day events
const getExclusiveEnd = (start: string, end: string, allDay: boolean) => {
  if (!allDay) return end;
  // For all-day/multi-day, add one day to the end date
  const endDate = new Date(end);
  endDate.setDate(endDate.getDate() + 1);
  return endDate.toISOString().split('T')[0];
};

function toMapCoord(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Resolve map pin address/coordinates the same way mobile booking creation does. */
function resolveBookingMapLocation(booking: Record<string, any>) {
  const rawCustomer = booking.customer;
  const customer = (
    Array.isArray(rawCustomer) ? rawCustomer[0] : rawCustomer
  ) as Record<string, unknown> | null | undefined;
  let address =
    typeof booking.booking_address === 'string' && booking.booking_address.trim()
      ? booking.booking_address.trim()
      : null;
  let lat = toMapCoord(booking.booking_lat);
  let lng = toMapCoord(booking.booking_lng);

  if (customer) {
    if (lat == null) lat = toMapCoord(customer.lat);
    if (lng == null) lng = toMapCoord(customer.lng);

    if (!address && typeof customer.address === 'string' && customer.address.trim()) {
      address = customer.address.trim();
    }

    const extras = parseCustomerAdditionalAddresses(customer.additional_addresses);
    for (const ex of extras) {
      const exAddr = ex.address?.trim();
      const exLat = toMapCoord(ex.lat);
      const exLng = toMapCoord(ex.lng);
      if (lat == null && exLat != null && exLng != null) {
        lat = exLat;
        lng = exLng;
        if (exAddr) address = exAddr;
        break;
      }
      if (!address && exAddr) {
        address = exAddr;
      }
    }
  }

  return { address, lat, lng };
}

export async function getCalendarBookings(businessId: string, startDate: Date, endDate: Date, isLocationBooking: boolean, filterByResourceId?: string): Promise<CalendarBooking[]> {
  if (!supabase) throw new Error('Supabase client is not initialized');

  console.log('📅 getCalendarBookings called with:', {
    businessId,
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    isLocationBooking,
    filterByResourceId,
    isFiltering: !!filterByResourceId
  });

  // Format dates for the query
  const formattedStartDate = startDate.toISOString().split('T')[0];
  const formattedEndDate = endDate.toISOString().split('T')[0];

  console.log('Formatted dates for query:', {
    formattedStartDate,
    formattedEndDate
  });

  let bookings;
  let error;

  if (filterByResourceId) {
    console.log('📅 Filtering by resource ID:', filterByResourceId);
    // If filtering by resource, we need to first get the booking IDs that have this resource
    const { data: resourceBookings, error: resourceError } = await supabase
      .from('booking_resources')
      .select('booking_id')
      .eq('resource_id', filterByResourceId);

    console.log('📅 Resource bookings query result:', {
      resourceBookings,
      resourceError,
      bookingCount: resourceBookings?.length || 0
    });

    if (resourceError) {
      error = resourceError;
      bookings = null;
    } else {
      const bookingIds = resourceBookings?.map(rb => rb.booking_id) || [];
      console.log('📅 Found booking IDs for resource:', bookingIds);
      
      if (bookingIds.length === 0) {
        console.log('📅 No bookings found for this resource - showing empty calendar');
        // No bookings for this resource
        bookings = [];
        error = null;
      } else {
        // Get the full booking data for these booking IDs
        const { data: fullBookings, error: bookingsError } = await supabase
          .from('bookings')
          .select(`
            *,
            source,
            customer:customers!bookings_customer_id_fkey(
              id,
              first_name,
              last_name,
              email,
              phone,
              address,
              lat,
              lng,
              additional_addresses
            ),
            services:booking_services(
              *,
              service:services(*),
              vehicle:customer_vehicles(*)
            ),
            payments:payments_with_stripe_details(*),
            resources:booking_resources(
              *,
              resource:resources(
                id,
                first_name,
                last_name,
                avatar_url,
                email,
                role_id,
                permission_role:resource_roles(
                  id,
                  name
                )
              )
            ),
            booking_assets(
              *,
              asset:assets(id, name, asset_type, image_url, status)
            ),
            booking_workflows(
              id,
              name,
              service_id,
              steps:booking_workflow_steps(
                id,
                workflow_step_library_id,
                title,
                description,
                order_index,
                status,
                completed_at,
                notes,
                assigned_resource_id
              )
            )
          `)
          .eq('business_id', businessId)
          .eq('mobile_booking', !isLocationBooking)
          .eq('is_deleted', false)
      .or('in_que.is.null,in_que.eq.false')
          .or('hidden.is.null,hidden.eq.false')
          .lte('start_date', formattedEndDate)  // Booking starts before or on end of range
          .gte('end_date', formattedStartDate)  // Booking ends on or after start of range
          .in('id', bookingIds)
          .order('start_date', { ascending: true });

        bookings = fullBookings;
        error = bookingsError;
      }
    }
  } else {
    // Normal query without resource filtering
    const { data: allBookings, error: bookingsError } = await supabase
      .from('bookings')
      .select(`
        *,
        source,
        customer:customers!bookings_customer_id_fkey(
          id,
          first_name,
          last_name,
          email,
          phone,
          address,
          lat,
          lng,
          additional_addresses
        ),
        services:booking_services(
          *,
          workflow_steps,
          service:services(*),
          vehicle:customer_vehicles(*)
        ),
        payments:payments_with_stripe_details(*),
        resources:booking_resources(
          *,
          resource:resources(
            id,
            first_name,
            last_name,
            avatar_url,
            email,
            compensation_type,
            commission_rate,
            commission_percentage,
            role_id,
            permission_role:resource_roles(
              id,
              name
            )
          )
        ),
        booking_assets(
          *,
          asset:assets(id, name, asset_type, image_url, status)
        ),
        booking_workflows(
          id,
          name,
          service_id,
          steps:booking_workflow_steps(
            id,
            workflow_step_library_id,
            title,
            description,
            order_index,
            status,
            completed_at,
            notes,
            assigned_resource_id
          )
        )
      `)
      .eq('business_id', businessId)
      .eq('mobile_booking', !isLocationBooking)
      .eq('is_deleted', false)
    .or('in_que.is.null,in_que.eq.false')
      .or('hidden.is.null,hidden.eq.false')
      .lte('start_date', formattedEndDate)  // Booking starts before or on end of range
      .gte('end_date', formattedStartDate)  // Booking ends on or after start of range
      .order('start_date', { ascending: true });

    bookings = allBookings;
    error = bookingsError;
  }

  if (error) {
    console.error('Error fetching calendar bookings:', error);
    throw error;
  }

  if (!bookings) return [];
  
  // Get color based on status
  const getStatusColor = (status: string, booking: any) => {
    // For warranty bookings, always use grey regardless of status
    if (booking.is_warranty) {
      return '#6b7280'; // Grey (gray-500)
    }
    
    // For cancelled bookings, always use pale red regardless of any other colors
    if (status === 'cancelled') {
      return '#fee2e2'; // Pale red (red-100)
    }
    
    // For scheduled bookings, use the booking's color if available
    if (status === 'scheduled' && booking.color) {
      return booking.color;
    }
    
    // For in_progress, always use yellow
    if (status === 'in_progress') {
      return '#eab308'; // Yellow
    }
    
    // Use default status colors for other statuses
    switch (status) {
      case 'pending':
        return '#a855f7'; // Purple
      case 'scheduled':
        return '#3b82f6'; // Blue (default)
      case 'completed':
        return '#22c55e'; // Green
      default:
        return '#3b82f6'; // Default blue
    }
  };

  // Process regular bookings
  const regularBookings = bookings.map(booking => {
    // Ensure valid start and end dates/times
    const start = formatDateTime(booking.start_date, booking.start_time);
    let end = booking.end_date && booking.end_time
      ? formatDateTime(booking.end_date, booking.end_time)
      : formatDateTime(booking.start_date, booking.start_time);
    // If multi-day (end date > start date), and times are 00:00, treat as all-day
    let allDay = false;
    if (booking.start_date !== booking.end_date) {
      const startTime = booking.start_time;
      const endTime = booking.end_time;
      if ((startTime === '00:00' || startTime === '00:00:00') && (endTime === '00:00' || endTime === '00:00:00')) {
        allDay = true;
        // For all-day/multi-day, set end to exclusive day after
        end = getExclusiveEnd(start, end, true);
      }
    }

    // Get the color based on status and service
    const color = getStatusColor(booking.status, booking);

    // Transform resources array to match the expected format
    const resources = booking.resources?.map((br: any) => ({
      id: br.resource.id,
      first_name: br.resource.first_name,
      last_name: br.resource.last_name,
      avatar_url: br.resource.avatar_url
    })) || [];

    // Transform booking_assets to get assigned asset info
    const bookingAssets = booking.booking_assets?.map((ba: any) => ({
      id: ba.asset_id,
      asset_id: ba.asset_id,
      name: ba.asset?.name || 'Asset',
      asset_type: ba.asset?.asset_type || null,
      image_url: ba.asset?.image_url || null,
    })) || [];

    // Get the primary resource ID: prefer resource, fall back to asset ID
    // Asset IDs work because CalendarV2 maps assets to Resource-compatible objects with id = asset.id
    const resourceId = resources.length > 0 
      ? resources[0].id 
      : (bookingAssets.length > 0 ? bookingAssets[0].id : null);

    const mapLocation = resolveBookingMapLocation(booking);

    const processedBooking = {
      id: booking.id,
      title: booking.title || `Booking #${booking.booking_number}`,
      booking_number: booking.booking_number,
      start,
      end,
      backgroundColor: color,
      borderColor: color,
      secondaryColor: booking.secondary_color || null,
      allDay,
      display: 'block',
      resourceId,
      extendedProps: {
        customer: booking.customer 
          ? `${booking.customer.first_name} ${booking.customer.last_name}`
          : 'No Customer',
        customer_phone: booking.customer?.phone || null,
        customer_email: booking.customer?.email || null,
        customer_id: booking.customer?.id || null,
        status: booking.status,
        booking_number: booking.booking_number,
        subtotal: booking.subtotal || 0,
        total_amount: booking.total_amount || 0,
        discount_amount: booking.discount_amount || 0, // Add discount_amount for sales calculation
        start_date: booking.start_date || null,
        start_time: booking.start_time || null,
        end_date: booking.end_date || null, // Add end_date for sales calculation
        end_time: booking.end_time || null,
        drop_off_date: booking.drop_off_date || null,
        drop_off_time: booking.drop_off_time || null,
        payments: booking.payments || [],
        services: booking.services || [],
        resources: resources,
        booking_assets: bookingAssets,
        is_buffer_block: false,
        mobile_booking: booking.mobile_booking,
        resource_id: resourceId,
        customer_notes: booking.customer_notes,
        secondaryColor: booking.secondary_color || null,
        booking_address: mapLocation.address,
        booking_workflows: booking.booking_workflows || [],
        address: mapLocation.address,
        lat: mapLocation.lat,
        lng: mapLocation.lng,
      }
    };

    return processedBooking;
  });

  return regularBookings;
}

export interface BookingService {
  id: string;
  booking_id: string;
  business_id: string;
  service_id: string;
  vehicle_id: string;
  name: string;
  description: string | null;
  price: number;
  tax_rate: number;
  tax_exempt: boolean;
  tax_amount: number;
  total_amount: number;
  duration_minutes: number;
  status: string;
  notes: string | null;
  start_date: string | null;
  end_date: string | null;
  vehicle_make: string | null;
  vehicle_model: string | null;
  vehicle_year: number | null;
  vehicle_color: string | null;
  vehicle_vin: string | null;
  vehicle_license_plate: string | null;
  discount_amount?: number;
  pricing_type?: 'flat' | 'per_unit' | 'per_sqft';
  unit_price?: number;
  duration_per_unit?: number;
  quantity?: number;
  compare_at_price?: number | null;
  addons?: ServiceAddon[];
  created_at?: string;
  updated_at?: string;
}

interface BookingResource {
  id: string;
  booking_id: string;
  resource_id: string;
  service_id: string | null;
  role: string;
  start_date: string | null;
  end_date: string | null;
  created_at: string;
  updated_at: string;
  resource?: {
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
    avatar_url?: string;
  };
}

export interface BookingAsset {
  id: string;
  booking_id: string;
  asset_id: string;
  start_date: string | null;
  end_date: string | null;
  created_at: string;
  updated_at: string;
  asset?: {
    id: string;
    name: string;
    asset_type?: string;
    image_url?: string;
    status?: string;
  };
}

interface BookingFlatRate {
  id: string;
  booking_id: string;
  item_id: string;
  resource_id: string;
  hours: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
  item_name?: string;
  item_description?: string;
  item_hours?: number;
  resource_first_name?: string;
  resource_last_name?: string;
  resource_email?: string;
}

export interface BookingMaterial {
  id: string;
  booking_id: string;
  material_id: string | null; // Null for inventory items
  item_id: string | null; // Reference to stock_items (null for regular materials)
  material_name: string;
  material_description?: string;
  quantity: number;
  unit: string;
  cost: number;
  total_cost: number;
  created_at: string;
  updated_at: string;
}

export interface BookingDiscount {
  id: string;
  booking_id: string;
  title: string;
  type: string;
  amount: number;
  created_at: string;
  updated_at: string;
}

export interface Payment {
  id: string;
  booking_id: string;
  amount: number;
  status: string;
  created_at: string;
  updated_at: string;
  pdf_url?: string;
  method?: string;
  reference?: string;
  notes?: string;
}

export interface Booking {
  id: string;
  title: string;
  status: string;
  subtotal: number;
  discount_amount?: number;
  end_date?: string;
  booking_number?: string;
  flat_rate_earnings?: number;
  estimated_flat_rate_earnings?: number;
  hourly_earnings?: number;
  estimated_hourly_earnings?: number;
  supplies_cost?: number;
  fixed_expenses?: number;
  services?: BookingService[];
  customer?: {
    id: string;
    created_at: string;
    first_name?: string;
    last_name?: string;
    email?: string | null;
    phone?: string;
    vehicles?: CustomerVehicle[];
  };
  resources?: BookingResource[];
  booking_assets?: BookingAsset[];
  materials?: BookingMaterial[];
  discounts?: BookingDiscount[];
  payments?: Payment[];
  flat_rates?: BookingFlatRate[];
  tax_amount?: number;
  total_amount?: number;
  business_id: string;
  booking_address?: string;
  booking_lat?: number | null;
  booking_lng?: number | null;
  mobile_booking?: boolean;
  created_at: string;
  updated_at?: string;
  completed_at?: string;
  previous_status?: string;
  feedback_token?: string;
  
  // Additional fields for bookings
  color?: string;
  secondary_color?: string;
  drop_off_date?: string;
  drop_off_time?: string;
  start_date?: string;
  start_time?: string;
  end_time?: string;
  notes?: string;
  customer_notes?: string;
  source?: string;
  customer_repeat_status?: string;
  referral_source_customer_id?: string | null;
  referral_reward_processed_at?: string | null;
  created_by?: string;
  
  // Liability waiver and damage report data (preloaded)
  liability_waiver_enabled?: boolean;
  liability_waiver?: any; // LiabilityWaiver type from liabilityWaivers.ts
  damage_report?: any; // VehicleDamageReport type from damageReports.ts
  
  // Workflow data
  booking_workflows?: Array<{
    id: string;
    name?: string;
    service_id?: string;
    steps?: Array<{
      id: string;
      workflow_step_library_id?: string;
      title: string;
      description?: string;
      order_index: number;
      status: string;
      completed_at?: string;
      notes?: string;
      assigned_resource_id?: string;
      resource?: {
        id: string;
        first_name: string;
        last_name: string;
        avatar_url?: string;
      };
    }>;
  }>;
  
  // Warranty booking fields
  is_warranty?: boolean;
  original_booking_id?: string;
  
  // Membership booking fields
  membership_id?: string;
  
  // Project link
  project_id?: string | null;

  // Unscheduled ("limbo") booking — created/approved without a date yet
  is_unscheduled?: boolean;
}

interface CustomerVehicle {
  id: string;
  customer_id: string;
  make: string;
  model: string;
  year: number;
  vin: string;
  color: string;
  license_plate: string;
  is_primary: boolean;
  created_at: string;
  updated_at: string;
}

export async function getBookings(businessId: string, options?: { 
  limit?: number; 
  offset?: number; 
  startDate?: Date; 
  endDate?: Date; 
  customerId?: string;
  /** Filter to a single booking status (e.g. 'in_progress'). */
  status?: string;
}) {
  if (!businessId || typeof businessId !== 'string') {
    throw new Error('Valid business ID is required');
  }

  const { limit, offset = 0, startDate, endDate, customerId, status } = options || {};

  let query = supabase
    .from('bookings')
    .select(`
      *,
      customer:customers!bookings_customer_id_fkey(
        id,
        first_name,
        last_name,
        email,
        phone
      ),
      discounts:booking_discounts(*),
      services:booking_services(*),
      resources:booking_resources(
        *,
        resource:resources(
          id,
          first_name,
          last_name,
          email,
          avatar_url,
          compensation_type,
          commission_rate,
          commission_percentage,
          role_id,
          permission_role:resource_roles(
            id,
            name
          )
        )
      ),
      flat_rates:booking_flat_rates_with_details(*),
      payments:payments_with_stripe_details(*)
    `)
    .eq('business_id', businessId)
    .eq('is_deleted', false);

  // Add date filtering if provided
  if (startDate) {
    const formattedStartDate = startDate.toISOString().split('T')[0];
    query = query.gte('start_date', formattedStartDate);
  }
  
  if (endDate) {
    const formattedEndDate = endDate.toISOString().split('T')[0];
    query = query.lte('start_date', formattedEndDate);
  }

  // Add customer filtering if provided
  if (customerId) {
    query = query.eq('customer_id', customerId);
  }

  if (status) {
    query = query.eq('status', status);
  }

  query = query.order('created_at', { ascending: false });

  // Only apply range if limit is specified
  if (limit !== undefined) {
    query = query.range(offset, offset + limit - 1);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching bookings:', error);
    throw error;
  }

  return data as Booking[];
}

export async function getBookingsByCustomer(businessId: string, customerId: string, options?: { limit?: number; offset?: number }) {
  if (!businessId || typeof businessId !== 'string') {
    throw new Error('Valid business ID is required');
  }
  
  if (!customerId || typeof customerId !== 'string') {
    throw new Error('Valid customer ID is required');
  }

  const { limit = 50, offset = 0 } = options || {};

  const { data, error } = await supabase
    .from('bookings')
    .select(`
      *,
      customer:customers!bookings_customer_id_fkey(
        id,
        first_name,
        last_name,
        email,
        phone
      ),
      discounts:booking_discounts(*),
      services:booking_services(*),
      resources:booking_resources(
        *,
        resource:resources(
          id,
          first_name,
          last_name,
          email,
          avatar_url,
          compensation_type,
          commission_rate,
          commission_percentage,
          role_id,
          permission_role:resource_roles(
            id,
            name
          )
        )
      ),
      flat_rates:booking_flat_rates_with_details(*),
      payments:payments_with_stripe_details(*)
    `)
    .eq('business_id', businessId)
    .eq('customer_id', customerId)
    .eq('is_deleted', false)
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) {
    console.error('Error fetching bookings for customer:', error);
    throw error;
  }

  return data as Booking[];
}

// Get total count of bookings for pagination
export async function getBookingsCount(businessId: string): Promise<number> {
  if (!businessId || typeof businessId !== 'string') {
    throw new Error('Valid business ID is required');
  }

  const { count, error } = await supabase
    .from('bookings')
    .select('*', { count: 'exact', head: true })
    .eq('business_id', businessId);

  if (error) {
    console.error('Error counting bookings:', error);
    throw error;
  }

  return count || 0;
}

export async function getBooking(id: string) {
  if (!id || typeof id !== 'string') {
    throw new Error('Valid booking ID is required');
  }

  const { data, error } = await supabase
    .from('bookings')
    .select(`
      *,
      customer:customers!bookings_customer_id_fkey(
        id,
        first_name, 
        last_name, 
        email, 
        phone,
        vehicles:customer_vehicles(*)
      ),
      discounts:booking_discounts(*),
      services:booking_services(*,
        vehicle:customer_vehicles(make, model, year, color, vin, license_plate),
        addons:booking_service_addons(
          id,
          price,
          discount_type,
          discount_value,
          created_at,
          updated_at,
          addon:service_addons(*)
        )
      ),
      materials:booking_materials_with_details(*),
      resources:booking_resources(
        *,
        resource:resources(
          id,
          first_name,
          last_name,
          email,
          phone,
          avatar_url,
          compensation_type,
          commission_rate,
          commission_percentage,
          role_id,
          permission_role:resource_roles(
            id,
            name
          )
        )
      ),
      booking_assets(
        *,
        asset:assets(
          id,
          name,
          asset_type,
          image_url,
          status
        )
      ),
      flat_rates:booking_flat_rates_with_details(*),
      payments:payments_with_stripe_details(*),
      booking_workflows(
        id,
        name,
        service_id,
        steps:booking_workflow_steps(
          id,
          workflow_step_library_id,
          title,
          description,
          order_index,
          status,
          completed_at,
          notes,
          assigned_resource_id,
          resource:resources(
            id,
            first_name,
            last_name,
            avatar_url,
            auth_user_id
          )
        )
      )
    `)
    .eq('id', id)
    .eq('is_deleted', false)
    .limit(1);

  if (error) {
    console.error('Error fetching booking:', {
      id,
      error: error.message,
      details: error.details,
      hint: error.hint
    });
    throw error;
  }

  const row = Array.isArray(data) ? data[0] : data;
  if (!row) {
    const notFoundError = new Error('Booking not found');
    (notFoundError as any).code = 'PGRST116';
    throw notFoundError;
  }
  // Validate customer data
  if (!row.customer || !row.customer.id) {
    console.error('Invalid customer data:', {
      bookingId: id,
      customer: row.customer
    });
    throw new Error(`Invalid customer data for booking ${id}`);
  }

  const booking = row as Booking;
  
  // Preload liability waiver and damage report data
  try {
    // Import the liability waiver functions
    const { isLiabilityWaiverEnabled, getLiabilityWaiverByBookingId } = await import('./liabilityWaivers');
    const { getDamageReportByBookingId } = await import('./damageReports');
    
    // Load liability waiver data in parallel
    const [isLiabilityEnabled, liabilityWaiver, damageReport] = await Promise.all([
      isLiabilityWaiverEnabled(booking.business_id),
      getLiabilityWaiverByBookingId(id),
      getDamageReportByBookingId(id)
    ]);
    
    // Add liability data to booking
    booking.liability_waiver_enabled = isLiabilityEnabled;
    booking.liability_waiver = liabilityWaiver;
    booking.damage_report = damageReport;
    
  } catch (liabilityError) {
    console.error('Error loading liability data:', liabilityError);
    // Continue without liability data if there's an error
    booking.liability_waiver_enabled = false;
    booking.liability_waiver = null;
    booking.damage_report = null;
  }

  // Transform nested data to match expected interfaces
  if (booking.services) {
    booking.services = booking.services.map(service => {
      // Transform nested addon structure to flat structure expected by components
      if (service.addons && Array.isArray(service.addons)) {
        service.addons = service.addons.map((addonRecord: any) => ({
          id: addonRecord.addon?.id || addonRecord.id,
          name: addonRecord.addon?.name || 'Unknown Add-on',
          description: addonRecord.addon?.description || null,
          price: addonRecord.price,
          discount_type: addonRecord.discount_type || null,
          discount_value: addonRecord.discount_value ?? null,
          duration_minutes: addonRecord.addon?.duration_minutes || 0,
          order_index: addonRecord.addon?.order_index || 0,
          created_at: addonRecord.created_at,
          updated_at: addonRecord.updated_at
        }));
      }
      
      // Transform nested vehicle structure to flat structure expected by components
      if (service.vehicle && typeof service.vehicle === 'object') {
        service.vehicle_make = service.vehicle.make || null;
        service.vehicle_model = service.vehicle.model || null;
        service.vehicle_year = service.vehicle.year || null;
        service.vehicle_color = service.vehicle.color || null;
        service.vehicle_vin = service.vehicle.vin || null;
        service.vehicle_license_plate = service.vehicle.license_plate || null;
      }
      
      return service;
    });
  }

  return booking;
}

interface CreateBookingParams {
  business_id: string;
  customer_id: string;
  vehicles?: {
    vehicle_id: string | null;
    services: {
      service_id?: string;
      name: string;
      description?: string;
      price: number;
      tax_exempt?: boolean;
      tax_rate?: number;
      tax_amount?: number;
      discount_amount?: number;
      discount_type?: string;
      discount_value?: number;
      total_amount?: number;
      duration_minutes: number;
      start_date?: string;
      end_date?: string;
      notes?: string;
      resource_id?: string;
      workflow_steps?: any;
      pricing_type?: 'flat' | 'per_unit' | 'per_sqft';
      unit_price?: number;
      duration_per_unit?: number;
      quantity?: number;
    }[];
    resource_id?: string;
  }[];
  resources?: {
    resource_id: string;
    service_id?: string;
    role?: string;
    start_date?: string;
    end_date?: string;
  }[];
  resource_id?: string;
  resource_ids?: string[];
  materials?: BookingMaterial[];
  flat_rates?: BookingFlatRate[];
  discounts?: BookingDiscount[];
  title?: string;
  status?: string;
  /** When true, create the booking with no date assigned ("limbo" state). */
  is_unscheduled?: boolean;
  /** Force mobile_booking flag (used for unscheduled mobile bookings). */
  mobile_booking?: boolean;
  color?: string;
  secondary_color?: string;
  drop_off_date?: string;
  drop_off_time?: string;
  start_date?: string;
  start_time?: string;
  end_date?: string;
  end_time?: string;
  location?: string;
  notes?: string;
  booking_address?: string;
  booking_lat?: number | null;
  booking_lng?: number | null;
  customer_notes?: string;
  buffer_time?: number;
  customer_repeat_status?: string;
  source?: string;
  referral_source_customer_id?: string;
  created_by?: string;
  workflow_assignments?: {
    service_id: string;
    workflow_id: string;
    workflow_name?: string;
    assignments: {
      stepId: string;
      stepTitle: string;
      stepDescription?: string;
      stepCategory?: string;
      workflowStepLibraryId?: string;
      assignedResources: {
        resourceId: string;
        resourceName: string;
        avatarUrl?: string;
      }[];
    }[];
  }[];
  // Warranty booking fields
  is_warranty?: boolean;
  original_booking_id?: string;
  // Project link
  project_id?: string | null;
  // Opportunity link
  pipeline_opportunities_id?: number | null;
  // Field quote origin link(s) — booking_field_quotes join rows
  field_quote_ids?: string[];
  // Proposal origin link — lets the invoice engine adopt the proposal's deposit invoice
  source_proposal_id?: string | null;
  // When true, this booking is excluded from all automations (legacy + V2)
  automations_disabled?: boolean;
  // Asset assignment
  asset_id?: string;
  asset_ids?: string[];
  // Recurring booking series this booking belongs to (see src/lib/bookingSeries.ts)
  booking_series_id?: string | null;
}

export async function createBooking(
  params: CreateBookingParams
) {
  // Unscheduled bookings keep their date fields NULL; guard date interpolations
  // so we never write "undefinedT..." into the date columns.
  const resourceStart = params.start_date && params.start_time ? `${params.start_date}T${params.start_time}` : null;
  const resourceEnd = params.end_date && params.end_time ? `${params.end_date}T${params.end_time}` : null;
  const serviceStart = params.start_date && params.start_time ? `${params.start_date} ${params.start_time}` : null;
  const serviceEnd = params.end_date && params.end_time ? `${params.end_date} ${params.end_time}` : null;

  // Create booking
  const { data: bookingData, error: bookingError } = await supabase
    .from('bookings')
    .insert([{
      business_id: params.business_id,
      customer_id: params.customer_id,
      title: params.title,
      status: params.status || 'scheduled',
      is_unscheduled: params.is_unscheduled || false,
      mobile_booking: params.mobile_booking || false,
      color: params.color,
      secondary_color: params.secondary_color,
      drop_off_date: params.drop_off_date || null,
      drop_off_time: params.drop_off_time || null,
      start_date: params.start_date || null,
      start_time: params.start_time || null,
      end_date: params.end_date || null,
      end_time: params.end_time || null,
      location: params.location,
      notes: params.notes,
      customer_notes: params.customer_notes,
      booking_address: params.booking_address,
      customer_repeat_status: params.customer_repeat_status,
      source: params.source,
      referral_source_customer_id: params.referral_source_customer_id || null,
      created_by: params.created_by,
      is_warranty: params.is_warranty || false,
      original_booking_id: params.original_booking_id,
      project_id: params.project_id || null,
      pipeline_opportunities_id: params.pipeline_opportunities_id || null,
      source_proposal_id: params.source_proposal_id || null,
      automations_disabled: params.automations_disabled || false,
      booking_series_id: params.booking_series_id || null
    }])
    .select()
    .single();

  if (bookingError) throw bookingError;

  // Create status history entry
  const { error: statusError } = await supabase
    .from('booking_status_history')
    .insert({
      booking_id: bookingData.id,
      status: params.status || 'scheduled',
      business_id: params.business_id
    });

  if (statusError) {
    console.error('Error creating status history:', statusError);
    // Don't throw here as the booking was created successfully
  }

  // Link originating field quote(s) — non-fatal if it fails
  if (params.field_quote_ids?.length) {
    await linkFieldQuotesToBooking(bookingData.id, params.field_quote_ids, params.business_id);
  }

  // Auto-link to an open CRM opportunity when none was provided (skip warranties).
  if (!params.pipeline_opportunities_id && params.customer_id && !params.is_warranty) {
    await autoLinkBookingToOpenOpportunity(bookingData.id, params.business_id, params.customer_id);
  }

  // Create booking resources if provided
  if (params.resources?.length) {
    const { error: resourcesError } = await supabase
      .from('booking_resources')
      .insert(
        params.resources.map(resource => ({
          booking_id: bookingData.id,
          resource_id: resource.resource_id,
          role: resource.role || 'technician',
          start_date: resourceStart,
          end_date: resourceEnd
        }))
      );

    if (resourcesError) throw resourcesError;
  }

  // Create booking services for each vehicle if provided
  if (params.vehicles) {
    for (const vehicle of params.vehicles) {
      // Create services for this vehicle
      const { error: servicesError } = await supabase
        .from('booking_services')
        .insert(
          vehicle.services.map(service => {
            const taxRate = service.tax_rate || 5;
            // Per-line discount (carried from proposals/UI) reduces the taxable base.
            const discountAmount = Math.max(0, Math.min(service.discount_amount || 0, service.price));
            const discountedPrice = Math.max(0, service.price - discountAmount);
            const taxAmountFinal = service.tax_exempt ? 0 : Math.round((discountedPrice * taxRate / 100) * 100) / 100;
            const totalAmount = Math.round((discountedPrice + taxAmountFinal) * 100) / 100;

            // Log workflow data being saved
            if (service.workflow_steps) {
              console.log(`💾 Saving workflow data to booking_services for service "${service.name}":`, service.workflow_steps);
            }

            return {
              business_id: params.business_id,
              booking_id: bookingData.id,
              service_id: service.service_id,
              vehicle_id: vehicle.vehicle_id,
              name: service.name,
              description: service.description,
              price: service.price,
              discount_type: service.discount_type || 'fixed',
              discount_value: service.discount_value || 0,
              discount_amount: discountAmount,
              tax_exempt: service.tax_exempt || false,
              tax_rate: taxRate,
              tax_amount: taxAmountFinal,
              total_amount: totalAmount,
              duration_minutes: service.duration_minutes,
              start_date: serviceStart,
              end_date: serviceEnd,
              status: 'pending',
              notes: service.description,
              workflow_steps: service.workflow_steps || null,
              pricing_type: service.pricing_type || 'flat',
              unit_price: service.unit_price || null,
              duration_per_unit: service.duration_per_unit || null,
              quantity: service.quantity || 1,
              compare_at_price: service.compare_at_price ?? null
            };
          })
        );

      if (servicesError) throw servicesError;
    }
  }

  // Create booking resources for services
  if (params.vehicles) {
    for (const vehicle of params.vehicles) {
      for (const service of vehicle.services) {
        if (service.resource_id) {
          const { error: resourceError } = await supabase
            .from('booking_resources')
            .insert({
              booking_id: bookingData.id,
              resource_id: service.resource_id,
              service_id: service.service_id,
              role: 'technician',
              start_date: serviceStart,
              end_date: serviceEnd
            });

          if (resourceError) throw resourceError;
        }
      }
    }
  }

  // Create overall booking resource if specified
  if (params.resource_id) {
    const { error: resourcesError } = await supabase
      .from('booking_resources')
      .insert({
        booking_id: bookingData.id,
        resource_id: params.resource_id,
        role: 'lead',
        start_date: serviceStart,
        end_date: serviceEnd
      });

    if (resourcesError) throw resourcesError;
  }

  // Copy materials if provided
  if (params.materials?.length) {
    const { error: materialsError } = await supabase
      .from('booking_materials')
      .insert(
        params.materials.map(material => ({
          booking_id: bookingData.id,
          material_id: material.material_id,
          material_name: material.material_name,
          material_description: material.material_description,
          quantity: material.quantity,
          unit: material.unit,
          cost: material.cost,
          total_cost: material.total_cost
        }))
      );

    if (materialsError) throw materialsError;
  }

  // Copy flat rates if provided
  if (params.flat_rates?.length) {
    const { error: flatRatesError } = await supabase
      .from('booking_flat_rates')
      .insert(
        params.flat_rates.map(rate => ({
          booking_id: bookingData.id,
          item_id: rate.item_id,
          resource_id: rate.resource_id,
          hours: rate.hours,
          notes: rate.notes
        }))
      );

    if (flatRatesError) throw flatRatesError;
  }

  // Copy discounts if provided
  if (params.discounts?.length) {
    const { error: discountsError } = await supabase
      .from('booking_discounts')
      .insert(
        params.discounts.map(discount => ({
          booking_id: bookingData.id,
          title: discount.title,
          type: discount.type,
          amount: discount.amount
        }))
      );

    if (discountsError) throw discountsError;
  }

  // Create booking workflows if provided
  if (params.workflow_assignments?.length) {
    for (const workflowAssignment of params.workflow_assignments) {
      // Only create if there are actual assignments
      if (workflowAssignment.assignments.some(a => a.assignedResources.length > 0)) {
        try {
          // Use the new function that doesn't create hard dependencies
          const steps = workflowAssignment.assignments.flatMap((assignment, index) =>
            assignment.assignedResources.map(resource => {
              // Validate workflow_step_library_id
              const libraryId = assignment.workflowStepLibraryId || assignment.stepId;
              const isValidUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(libraryId || '');
              
              return {
                workflow_step_library_id: isValidUUID ? libraryId : null, // Only pass valid UUIDs
                assigned_resource_id: resource.resourceId,
                title: assignment.stepTitle,
                description: assignment.stepDescription,
                order_index: index
              };
            })
          );

          await createBookingWorkflowWithStepsV2(
            bookingData.id,
            params.business_id,
            workflowAssignment.workflow_name || 'Workflow', // Use workflow name if provided
            workflowAssignment.service_id,
            steps
          );
        } catch (workflowError) {
          console.error('Error creating booking workflow:', workflowError);
          // Don't throw here as the booking was created successfully
        }
      }
    }
  }

  // Create booking scheduled milestone for tracking
  try {
    // If the booking has a scheduled date/time, create a scheduled milestone
    if (params.start_date && params.start_time && params.status === 'scheduled') {
      await createBookingScheduledMilestone(
        params.business_id,
        params.customer_id,
        bookingData.id,
        params.start_date,
        params.start_time
      );
    }
  } catch (milestoneError) {
    console.error('Error creating booking milestone:', milestoneError);
    // Don't throw here as the booking was created successfully
  }

  // Note: Removed PDF generation code - no longer using the old invoicing system

  triggerGcalSync(bookingData.id, 'create', params.business_id);

  return getBooking(bookingData.id);
}

/**
 * Rebuilds all mobile_bookings rows (regular + buffer blocks) for a booking
 * based on the current booking_resources and booking_assets assignments.
 * Call this whenever resources or assets change on a mobile booking.
 */
export async function syncMobileBookingsForBooking(bookingId: string) {
  if (!supabase) throw new Error('Supabase client is not initialized');

  // Fetch booking data
  const { data: booking, error: bookingErr } = await supabase
    .from('bookings')
    .select('id, business_id, start_date, start_time, end_date, end_time, mobile_booking')
    .eq('id', bookingId)
    .single();

  if (bookingErr || !booking) {
    console.error('syncMobileBookings: could not fetch booking', bookingErr);
    return;
  }
  if (!booking.mobile_booking) return;

  // Fetch current resource assignments
  const { data: bookingResources } = await supabase
    .from('booking_resources')
    .select('resource_id')
    .eq('booking_id', bookingId);
  const resourceIds = (bookingResources || []).map(r => r.resource_id);

  // Fetch current asset assignments
  const { data: bookingAssets } = await supabase
    .from('booking_assets')
    .select('asset_id')
    .eq('booking_id', bookingId);
  const assetIds = (bookingAssets || []).map(a => a.asset_id);

  // Check existing buffer blocks to preserve buffer_time value
  const { data: existingBuffers } = await supabase
    .from('mobile_bookings')
    .select('buffer_time')
    .eq('booking_id', bookingId)
    .eq('is_buffer_block', true)
    .limit(1);
  const bufferTime = existingBuffers?.[0]?.buffer_time || 0;

  // Delete ALL existing mobile_bookings rows for this booking (regular + buffer)
  const { error: deleteErr } = await supabase
    .from('mobile_bookings')
    .delete()
    .eq('booking_id', bookingId);
  if (deleteErr) {
    console.error('syncMobileBookings: error deleting old rows', deleteErr);
    return;
  }

  // Re-insert regular mobile_bookings rows for each resource
  if (resourceIds.length > 0) {
    const regularRows = resourceIds.map(resourceId => ({
      business_id: booking.business_id,
      resource_id: resourceId,
      asset_id: null,
      booking_id: bookingId,
      start_date: booking.start_date,
      start_time: booking.start_time,
      end_date: booking.end_date,
      end_time: booking.end_time,
      buffer_time: 0,
      is_buffer_block: false
    }));
    const { error: insertErr } = await supabase
      .from('mobile_bookings')
      .insert(regularRows);
    if (insertErr) console.error('syncMobileBookings: error inserting regular rows', insertErr);
  }

  // Re-insert buffer blocks for resources and assets
  if (bufferTime > 0 && booking.end_time) {
    const [endHours, endMinutes] = booking.end_time.split(':').map(Number);
    const bufferStart = new Date();
    bufferStart.setHours(endHours, endMinutes, 0, 0);
    const bufferEnd = new Date(bufferStart);
    bufferEnd.setMinutes(bufferEnd.getMinutes() + bufferTime);

    const bufferStartTime = booking.end_time;
    const bufferEndTime = `${String(bufferEnd.getHours()).padStart(2, '0')}:${String(bufferEnd.getMinutes()).padStart(2, '0')}`;

    const bufferRows = [
      ...resourceIds.map(resourceId => ({
        business_id: booking.business_id,
        resource_id: resourceId,
        asset_id: null,
        booking_id: bookingId,
        start_date: booking.end_date,
        start_time: bufferStartTime,
        end_date: booking.end_date,
        end_time: bufferEndTime,
        buffer_time: bufferTime,
        is_buffer_block: true
      })),
      ...assetIds.map(assetId => ({
        business_id: booking.business_id,
        resource_id: null,
        asset_id: assetId,
        booking_id: bookingId,
        start_date: booking.end_date,
        start_time: bufferStartTime,
        end_date: booking.end_date,
        end_time: bufferEndTime,
        buffer_time: bufferTime,
        is_buffer_block: true
      }))
    ];

    if (bufferRows.length > 0) {
      const { error: bufferErr } = await supabase
        .from('mobile_bookings')
        .insert(bufferRows);
      if (bufferErr) console.error('syncMobileBookings: error inserting buffer blocks', bufferErr);
      else console.log(`syncMobileBookings: created ${bufferRows.length} buffer blocks`);
    }
  }

  console.log(`syncMobileBookings: synced booking ${bookingId} — ${resourceIds.length} resources, ${assetIds.length} assets, buffer=${bufferTime}min`);
}

export async function updateBooking(
  id: string,
  updates: Partial<Booking> & {
    services?: {
      id?: string;
      service_id: string;
      vehicle_id: string;
      name: string;
      description?: string;
      price: number;
      tax_exempt?: boolean;
      tax_rate?: number;
      tax_amount?: number;
      discount_amount?: number;
      total_amount?: number;
      duration_minutes: number;
      start_date?: string;
      end_date?: string;
      notes?: string;
      pricing_type?: 'flat' | 'per_unit' | 'per_sqft';
      unit_price?: number;
      duration_per_unit?: number;
      quantity?: number;
    }[];
    resources?: {
      id?: string;
      resource_id: string;
      service_id?: string;
      role?: string;
      start_date?: string;
      end_date?: string;
    }[];
    flat_rates?: {
      id?: string;
      item_id: string;
      resource_id: string;
      hours: number;
      notes?: string;
    }[];
  }
) {
  const { services, resources, flat_rates, ...bookingUpdates } = updates;

  console.log('Updating booking:', {
    id,
    updates: {
      bookingUpdates,
      services: services?.length,
      resources: resources?.length,
      flat_rates: flat_rates?.length
    }
  });

  // First check if this is a mobile booking and get business_id
  const { data: bookingData } = await supabase
    .from('bookings')
    .select('mobile_booking, business_id')
    .eq('id', id)
    .single();

  const isMobileBooking = bookingData?.mobile_booking;

  // Update booking
  const { error: bookingError } = await supabase
    .from('bookings')
    .update(bookingUpdates)
    .eq('id', id);

  if (bookingError) throw bookingError;

  // If this is a mobile booking and we're updating dates/times, rebuild all
  // mobile_bookings rows (regular + buffer blocks) so buffer blocks stay
  // aligned with the booking's new end time.
  if (isMobileBooking && (bookingUpdates.start_date || bookingUpdates.start_time || bookingUpdates.end_date || bookingUpdates.end_time)) {
    await syncMobileBookingsForBooking(id);
  }

  // Update services if provided
  if (services) {
    console.log('Updating services:', services.length);

    // Delete existing services
    const { error: deleteServicesError } = await supabase
      .from('booking_services')
      .delete()
      .eq('booking_id', id);

    if (deleteServicesError) throw deleteServicesError;

    // Insert new services
    if (services.length > 0) {
      const { error: servicesError } = await supabase
        .from('booking_services')
        .insert(services.map(service => {
          const taxAmount = service.tax_exempt ? 0 : Math.round((service.price * (service.tax_rate || 5) / 100) * 100) / 100;
          const discountAmount = service.discount_amount || 0;
          const taxAmountFinal = service.tax_exempt ? 0 : taxAmount;
          const totalAmount = Math.round((service.price - discountAmount + taxAmountFinal) * 100) / 100;

          return {
            ...service,
            booking_id: id,
            tax_amount: taxAmountFinal,
            total_amount: totalAmount
          };
        }));

      if (servicesError) {
        console.error('Error inserting services:', servicesError);
        throw servicesError;
      }
    }
  }

  // Update resources if provided
  if (resources) {
    console.log('Updating resources:', resources.length);

    // Delete existing resources
    const { error: deleteResourcesError } = await supabase
      .from('booking_resources')
      .delete()
      .eq('booking_id', id);

    if (deleteResourcesError) throw deleteResourcesError;

    // Insert new resources
    if (resources.length > 0) {
      const { error: resourcesError } = await supabase
        .from('booking_resources')
        .insert(
          resources.map(resource => ({
            ...resource,
            booking_id: id
          }))
        );

      if (resourcesError) throw resourcesError;
    }

    // Sync mobile_bookings (regular rows + buffer blocks) for mobile bookings
    if (isMobileBooking) {
      await syncMobileBookingsForBooking(id);
    }
  }

  // Update flat rates if provided
  if (flat_rates) {
    console.log('Updating flat rates:', {
      newFlatRatesCount: flat_rates.length
    });

    try {
      // First delete all existing flat rates
      const { error: deleteError } = await supabase
        .from('booking_flat_rates')
        .delete()
        .eq('booking_id', id);

      if (deleteError) {
        console.error('Error deleting flat rates:', deleteError);
        throw deleteError;
      }

      // Then insert the new flat rates
      const { data: insertedRates, error: insertError } = await supabase
        .from('booking_flat_rates')
        .insert(
          flat_rates.map(flatRate => ({
            booking_id: id,
            item_id: flatRate.item_id,
            resource_id: flatRate.resource_id,
            hours: flatRate.hours,
            notes: flatRate.notes
          }))
        )
        .select();

      if (insertError) {
        console.error('Error inserting flat rates:', insertError);
        throw insertError;
      }

      console.log('Flat rates updated successfully:', {
        insertedCount: insertedRates?.length || 0
      });
    } catch (err) {
      console.error('Error updating flat rates:', err);
      throw err;
    }
  }

  // Note: Removed PDF generation code - no longer using the old invoicing system

  console.log('Booking update completed, fetching updated booking');
  if (bookingData?.business_id) {
    triggerGcalSync(id, 'update', bookingData.business_id);
  }
  return getBooking(id);
}

export async function deleteBooking(id: string) {
  try {
    console.log('Starting deletion of booking:', id);

    if (!supabase) {
      throw new Error('Supabase client is not initialized');
    }

    const client = supabase;
    
    // HARD DELETE: Remove mobile booking record (for resource scheduling)
    const { error: mobileBookingError } = await client
      .from('mobile_bookings')
      .delete()
      .eq('booking_id', id);

    // Don't throw error if mobile booking doesn't exist
    if (mobileBookingError && mobileBookingError.code !== 'PGRST116') {
      console.error('Error deleting mobile booking:', mobileBookingError);
    }

    // CLEANUP: Remove all products associated with this booking
    try {
      await cleanupBookingProducts(id);
    } catch (cleanupError) {
      console.error('Error cleaning up booking products:', cleanupError);
      // Don't throw here - we still want to delete the booking
    }

    // CLEANUP: Remove bonus entries for this booking
    try {
      const { removeBookingBonus } = await import('../services/bonusCalculationService');
      const bonusRemovalResult = await removeBookingBonus(id);
      if (bonusRemovalResult.success) {
        console.log(`Successfully removed ${bonusRemovalResult.entriesRemoved} bonus entries for deleted booking`);
      }
    } catch (bonusError) {
      console.error('Error removing bonus entries for deleted booking:', bonusError);
      // Don't throw here - we still want to delete the booking
    }

    // UNLINK PROJECT: If this booking is linked to a project, unlink it
    try {
      // Fetch the booking to check if it has a project_id
      const { data: bookingData, error: fetchError } = await client
        .from('bookings')
        .select('project_id')
        .eq('id', id)
        .single();

      if (!fetchError && bookingData?.project_id) {
        console.log('🔄 Unlinking project from deleted booking:', {
          bookingId: id,
          projectId: bookingData.project_id
        });

        // Update the project to remove booking_id and reset status
        const { error: projectUpdateError } = await client
          .from('projects')
          .update({
            booking_id: null,
            // Reset status to a default state if it was "Converted to booking"
            // We'll update status only if it's currently "Converted to booking"
            updated_at: new Date().toISOString()
          })
          .eq('id', bookingData.project_id);

        if (projectUpdateError) {
          console.error('❌ Error unlinking project from deleted booking:', projectUpdateError);
          // Don't throw here - we still want to delete the booking
        } else {
          // Check current project status and update if it was "Converted to booking"
          const { data: projectData, error: projectFetchError } = await client
            .from('projects')
            .select('status')
            .eq('id', bookingData.project_id)
            .single();

          if (!projectFetchError && projectData?.status === 'Converted to booking') {
            // Reset status to a default state
            const { error: statusUpdateError } = await client
              .from('projects')
              .update({
                status: 'Pending' // Reset to a default status
              })
              .eq('id', bookingData.project_id);

            if (statusUpdateError) {
              console.error('❌ Error updating project status:', statusUpdateError);
            } else {
              console.log('✅ Successfully reset project status from "Converted to booking"');
            }
          }

          console.log('✅ Successfully unlinked project from deleted booking');
        }
      } else if (fetchError) {
        console.error('❌ Error fetching booking to check project link:', fetchError);
        // Don't throw here - continue with deletion
      }
    } catch (projectUnlinkError) {
      console.error('❌ Error unlinking project from deleted booking:', projectUnlinkError);
      // Don't throw here - we still want to delete the booking
    }
    
    // Get business_id for gcal sync before soft delete
    const { data: bkData } = await client.from('bookings').select('business_id, google_event_id').eq('id', id).single();

    // SOFT DELETE: Mark main booking as deleted (preserve for automation system)
    const { error: bookingError } = await client
      .from('bookings')
      .update({ 
        is_deleted: true,
        updated_at: new Date().toISOString()
      })
      .eq('id', id);

    if (bookingError) {
      console.error('Error soft deleting booking:', bookingError);
      throw bookingError;
    }

    if (bkData?.business_id && bkData.google_event_id) {
      triggerGcalSync(id, 'delete', bkData.business_id);
    }

    console.log('Successfully deleted booking:', id, '(soft delete in bookings, hard delete in mobile_bookings)');
  } catch (err) {
    console.error('Error in deleteBooking:', err);
    throw err;
  }
}

async function progressBookingStatus(id: string) {
  // Get current booking status
  const { data: booking, error: fetchError } = await supabase
    .from('bookings')
    .select('status')
    .eq('id', id)
    .single();

  if (fetchError) throw fetchError;

  // Determine next status
  const nextStatus = (() => {
    switch (booking.status) {
      case 'pending':
        return 'scheduled';
      case 'scheduled':
        return 'in_progress';
      case 'in_progress':
        return 'completed';
      default:
        return booking.status;
    }
  })();

  // Update booking status
  const { error: updateError } = await supabase
    .from('bookings')
    .update({ status: nextStatus })
    .eq('id', id);

  if (updateError) throw updateError;

  return nextStatus;
}

async function getBookingStats(businessId: string) {
  const { data: bookings, error } = await supabase
    .from('bookings')
    .select(`
      *,
      services:booking_services(total_amount)
    `)
    .eq('business_id', businessId);

  if (error) throw error;

  const stats = {
    totalBookings: bookings.length,
    completedBookings: bookings.filter(b => b.status === 'completed').length,
    totalRevenue: bookings.reduce((sum, booking) => 
      sum + booking.services.reduce((serviceSum, service) => serviceSum + service.total_amount, 0)
    , 0),
    averageBookingValue: bookings.length > 0 
      ? bookings.reduce((sum, booking) => 
          sum + booking.services.reduce((serviceSum, service) => serviceSum + service.total_amount, 0)
        , 0) / bookings.length 
      : 0
  };

  return stats;
}

export async function createMobileBooking(params: CreateBookingParams) {
  if (!supabase) throw new Error('Supabase client is not initialized');

  try {
    console.log('Starting mobile booking creation with params:', {
      business_id: params.business_id,
      buffer_time: params.buffer_time,
      start_date: params.start_date,
      start_time: params.start_time,
      end_date: params.end_date,
      end_time: params.end_time
    });

    const resourceIds = params.resource_ids?.length
      ? params.resource_ids
      : (params.resource_id ? [params.resource_id] : []);

    const assetIds = params.asset_ids?.length
      ? params.asset_ids
      : (params.asset_id ? [params.asset_id] : []);

    // Unscheduled mobile bookings have no date/resource scheduling yet.
    const isUnscheduled = Boolean(params.is_unscheduled);
    const linkStart = params.start_date && params.start_time ? `${params.start_date} ${params.start_time}` : null;
    const linkEnd = params.end_date && params.end_time ? `${params.end_date} ${params.end_time}` : null;

    if (!isUnscheduled && resourceIds.length === 0 && assetIds.length === 0) {
      throw new Error('No resource or asset assigned for mobile booking');
    }

    // First create the booking record
    const { data: bookingData, error: bookingError } = await supabase
      .from('bookings')
      .insert([{
        business_id: params.business_id,
        customer_id: params.customer_id,
        location: null,
        notes: params.notes || null,
        customer_notes: params.customer_notes || null,
        subtotal: 0, // Will be updated after services are added
        discount_amount: 0,
        tax_amount: 0,
        total_amount: 0,
        drop_off_date: null,
        drop_off_time: null,
        start_date: isUnscheduled ? null : params.start_date,
        start_time: isUnscheduled ? null : params.start_time,
        end_date: isUnscheduled ? null : params.end_date,
        end_time: isUnscheduled ? null : params.end_time,
        is_unscheduled: isUnscheduled,
        title: params.title,
        status: 'scheduled',
        source: params.source || 'in_person',
        source_data: null,
        source_identifier: null,
        material_costs: 0,
        labour_costs: 0,
        completed_at: null,
        color: params.color,
        secondary_color: params.secondary_color,
        mobile_booking: true,
        booking_address: params.booking_address,
        booking_lat: params.booking_lat || null,
        booking_lng: params.booking_lng || null,
        customer_repeat_status: params.customer_repeat_status,
        created_by: params.created_by,
        project_id: params.project_id || null,
        pipeline_opportunities_id: params.pipeline_opportunities_id || null,
        source_proposal_id: params.source_proposal_id || null,
        automations_disabled: params.automations_disabled || false,
        booking_series_id: params.booking_series_id || null
      }])
      .select()
      .single();

    if (bookingError) throw bookingError;
    if (!bookingData) throw new Error('No booking data returned');

    // Link originating field quote(s) — non-fatal if it fails
    if (params.field_quote_ids?.length) {
      await linkFieldQuotesToBooking(bookingData.id, params.field_quote_ids, params.business_id);
    }

    // Auto-link to an open CRM opportunity when none was provided (skip warranties).
    if (!params.pipeline_opportunities_id && params.customer_id && !params.is_warranty) {
      await autoLinkBookingToOpenOpportunity(bookingData.id, params.business_id, params.customer_id);
    }

    console.log('Main booking record created with ID:', bookingData.id);
    
    // Create the mobile_booking records for resources only
    // Assets are tracked via booking_assets table (mobile_bookings.resource_id has FK to resources)
    const mobileBookingRows = resourceIds.map(resourceId => ({
      business_id: params.business_id,
      resource_id: resourceId,
      booking_id: bookingData.id,
      start_date: params.start_date,
      start_time: params.start_time,
      end_date: params.end_date,
      end_time: params.end_time,
      buffer_time: 0,
      is_buffer_block: false
    }));

    if (!isUnscheduled && mobileBookingRows.length > 0) {
      const { error: mobileBookingError } = await supabase
        .from('mobile_bookings')
        .insert(mobileBookingRows);

      if (mobileBookingError) {
        console.error('Error creating mobile booking record:', mobileBookingError);
        throw mobileBookingError;
      }
    }
    
    console.log('Main mobile_booking record created successfully');

    // Now explicitly check for buffer time and create a buffer block if needed
    const bufferTime = params.buffer_time !== undefined ? params.buffer_time : 0;
    console.log(`Buffer time for booking: ${bufferTime} minutes`);
    
    // If buffer time is set, create a separate buffer block (not for unscheduled — no date)
    if (!isUnscheduled && bufferTime > 0) {
      console.log(`Creating buffer block with buffer_time: ${bufferTime} minutes`);
      
      if (!params.end_time) {
        console.error('Cannot create buffer block: end_time is missing');
      } else {
        try {
          // Calculate the buffer block start and end times (starts right after the booking ends)
          // Parse the end time
          const [endHours, endMinutes] = params.end_time.split(':').map(Number);
          
          // Create a date object with the end time
          const bufferStartDate = new Date();
          bufferStartDate.setHours(endHours, endMinutes, 0, 0);
          
          // Create a date object for the end of the buffer
          const bufferEndDate = new Date(bufferStartDate);
          bufferEndDate.setMinutes(bufferEndDate.getMinutes() + bufferTime);
          
          // Format the times back to strings
          const bufferStartTime = params.end_time; // Use the exact end time of the booking as the start of the buffer
          const bufferEndTime = `${String(bufferEndDate.getHours()).padStart(2, '0')}:${String(bufferEndDate.getMinutes()).padStart(2, '0')}`;
          
          console.log(`Buffer block time range: ${bufferStartTime} - ${bufferEndTime}`);
          
          // Create buffer block rows for resources
          const resourceBufferBlocks = resourceIds.map(resourceId => ({
            business_id: params.business_id,
            resource_id: resourceId,
            asset_id: null,
            booking_id: bookingData.id,
            start_date: params.end_date,
            start_time: bufferStartTime,
            end_date: params.end_date,
            end_time: bufferEndTime,
            buffer_time: bufferTime,
            is_buffer_block: true
          }));

          // Create buffer block rows for assets
          const assetBufferBlocks = assetIds.map(assetId => ({
            business_id: params.business_id,
            resource_id: null,
            asset_id: assetId,
            booking_id: bookingData.id,
            start_date: params.end_date,
            start_time: bufferStartTime,
            end_date: params.end_date,
            end_time: bufferEndTime,
            buffer_time: bufferTime,
            is_buffer_block: true
          }));

          const allBufferBlocks = [...resourceBufferBlocks, ...assetBufferBlocks];
          
          if (allBufferBlocks.length === 0) {
            console.log('No buffer blocks to create');
          } else {
            console.log(`Creating ${allBufferBlocks.length} buffer blocks (${resourceBufferBlocks.length} resource, ${assetBufferBlocks.length} asset)`);
          
            const { error: bufferBlockError } = await supabase
              .from('mobile_bookings')
              .insert(allBufferBlocks);
            
            if (bufferBlockError) {
              console.error('Error creating buffer blocks:', bufferBlockError);
            } else {
              console.log('Buffer blocks created successfully');
            }
          }
        } catch (bufferError) {
          console.error('Error in buffer block creation:', bufferError);
          // Don't throw, just log the error
        }
      }
    } else {
      console.log('No buffer block created - buffer_time is', bufferTime);
    }

    // Create the booking_resources records
    if (resourceIds.length > 0) {
      const { error: bookingResourceError } = await supabase
        .from('booking_resources')
        .insert(resourceIds.map(resourceId => ({
          booking_id: bookingData.id,
          resource_id: resourceId,
          start_date: linkStart,
          end_date: linkEnd
        })));

      if (bookingResourceError) throw bookingResourceError;
    }

    // Create the booking_assets records
    if (assetIds.length > 0) {
      const { error: bookingAssetError } = await supabase
        .from('booking_assets')
        .insert(assetIds.map(assetId => ({
          booking_id: bookingData.id,
          asset_id: assetId,
          start_date: linkStart,
          end_date: linkEnd
        })));

      if (bookingAssetError) {
        console.error('Error creating booking_assets:', bookingAssetError);
      }
    }

    // Create booking services for each vehicle if provided
    if (params.vehicles) {
      let subtotal = 0;
      let taxAmount = 0;
      let hasAddons = false;

      for (const vehicle of params.vehicles) {
        // Create services for this vehicle
        const { error: servicesError } = await supabase
          .from('booking_services')
          .insert(
            vehicle.services.map(service => {
              const taxRate = service.tax_rate || 5;
              const taxAmountService = service.tax_exempt ? 0 : Math.round((service.price * taxRate / 100) * 100) / 100;
              const discountAmount = service.discount_amount || 0;
              const taxAmountFinal = service.tax_exempt ? 0 : taxAmountService;
              const totalAmount = Math.round((service.price - discountAmount + taxAmountFinal) * 100) / 100;

              subtotal += service.price;
              taxAmount += taxAmountFinal;

              if (service.workflow_steps) {
                console.log(`💾 Saving workflow data to booking_services for service "${service.name}":`, service.workflow_steps);
              }

              console.log(`📊 Creating booking_service record:`, {
                name: service.name,
                duration_minutes: service.duration_minutes,
                note: 'This should be service duration ONLY, not including buffer'
              });
              
              return {
                business_id: params.business_id,
                booking_id: bookingData.id,
                service_id: service.service_id,
                vehicle_id: vehicle.vehicle_id,
                name: service.name,
                description: service.description,
                price: service.price,
                tax_exempt: service.tax_exempt || false,
                tax_rate: taxRate,
                tax_amount: taxAmountFinal,
                discount_amount: discountAmount,
                total_amount: totalAmount,
                duration_minutes: service.duration_minutes,
                start_date: linkStart,
                end_date: linkEnd,
                status: 'pending',
                notes: service.notes,
                workflow_steps: service.workflow_steps || null,
                pricing_type: service.pricing_type || 'flat',
                unit_price: service.unit_price ?? null,
                duration_per_unit: service.duration_per_unit ?? null,
                quantity: service.quantity ?? 1,
                compare_at_price: service.compare_at_price ?? null
              };
            })
          );

        if (servicesError) throw servicesError;
      }

      // Handle add-ons after all services are inserted
      for (const vehicle of params.vehicles) {
        for (const service of vehicle.services) {
          if (service.addons && service.addons.length > 0) {
            const { data: serviceData, error: serviceQueryError } = await supabase
              .from('booking_services')
              .select('id')
              .eq('booking_id', bookingData.id)
              .eq('service_id', service.service_id)
              .eq('vehicle_id', vehicle.vehicle_id)
              .single();

            if (serviceQueryError) {
              console.error('Error finding booking service for add-ons:', serviceQueryError);
              continue;
            }

            if (serviceData?.id) {
              const addonsToInsert = service.addons.map((addon: any) => ({
                booking_service_id: serviceData.id,
                addon_id: addon.addon_id || addon.id,
                price: addon.price
              }));

              const { error: addonsError } = await supabase
                .from('booking_service_addons')
                .insert(addonsToInsert);

              if (addonsError) {
                console.error('Error inserting add-ons for service:', service.name, addonsError);
              } else {
                console.log(`Successfully inserted ${addonsToInsert.length} add-ons for service: ${service.name}`);
                hasAddons = true;
              }
            }
          }
        }
      }

      // Fetch additional per-service taxes so the booking total includes them
      const serviceIds = [...new Set(
        params.vehicles!.flatMap(v => v.services.map(s => s.service_id))
      )];
      let additionalTaxAmount = 0;
      if (serviceIds.length > 0) {
        const { data: addlTaxes } = await supabase
          .from('service_additional_taxes')
          .select('service_id, rate')
          .in('service_id', serviceIds);

        if (addlTaxes && addlTaxes.length > 0) {
          for (const vehicle of params.vehicles!) {
            for (const service of vehicle.services) {
              if (service.tax_exempt) continue;
              const svcAddlRates = addlTaxes.filter(t => t.service_id === service.service_id);
              for (const t of svcAddlRates) {
                additionalTaxAmount += Math.round((service.price * t.rate / 100) * 100) / 100;
              }
            }
          }
        }
      }

      // Update the booking with calculated totals (including additional per-service taxes)
      const totalTax = taxAmount + additionalTaxAmount;
      const totalAmount = subtotal + totalTax;
      const { error: updateError } = await supabase
        .from('bookings')
        .update({
          subtotal,
          tax_amount: totalTax,
          total_amount: totalAmount
        })
        .eq('id', bookingData.id);

      if (updateError) throw updateError;

      // If add-ons were inserted, recalculate totals via RPC so add-on prices
      // are included in subtotal/tax/total (the manual update above only covers services)
      if (hasAddons) {
        console.log('Recalculating booking totals to include add-on prices...');
        const { error: recalcError } = await supabase.rpc('calculate_booking_totals_manual', {
          target_booking_id: bookingData.id
        });
        if (recalcError) {
          console.error('Error recalculating booking totals after add-ons:', recalcError);
        } else {
          console.log('Booking totals recalculated with add-ons successfully');
        }
      }
    }

    console.log('Mobile booking creation completed successfully');
    
    // Create booking scheduled milestone for tracking
    try {
      // Create scheduled milestone for mobile bookings (they are always scheduled)
      if (params.start_date && params.start_time) {
        await createBookingScheduledMilestone(
          params.business_id,
          params.customer_id,
          bookingData.id,
          params.start_date,
          params.start_time
        );
      }
    } catch (milestoneError) {
      console.error('Error creating booking milestone for mobile booking:', milestoneError);
      // Don't throw here as the booking was created successfully
    }
    
    // Note: Removed PDF generation code - no longer using the old invoicing system

    // Reverse proximity check: if assigned resources are already near this
    // booking location, auto-start their job timers immediately.
    if (!isUnscheduled && params.booking_lat && params.booking_lng && resourceIds.length > 0) {
      supabase.functions.invoke('check-booking-proximity', {
        body: { booking_id: bookingData.id, business_id: params.business_id }
      }).catch(err => {
        console.error('Error calling check-booking-proximity:', err);
      });
    }

    triggerGcalSync(bookingData.id, 'create', params.business_id);

    return getBooking(bookingData.id);
  } catch (error) {
    console.error('Error creating mobile booking:', error);
    throw error;
  }
}

export async function getBasicBookingBlocks(businessId: string, startDate: Date, endDate: Date, isLocationBooking: boolean, filterByResourceId?: string): Promise<CalendarBooking[]> {
  if (!supabase) throw new Error('Supabase client is not initialized');

  // Format dates for the query
  const formattedStartDate = startDate.toISOString().split('T')[0];
  const formattedEndDate = endDate.toISOString().split('T')[0];

  // Fetch minimal fields needed for displaying blocks including customer name for better UX
  const { data: bookings, error } = await supabase
    .from('bookings')
    .select(`
      id, 
      booking_number, 
      title,
      start_date, 
      start_time, 
      end_date, 
      end_time, 
      status, 
      color, 
      secondary_color, 
      drop_off_time,
      customer:customers!bookings_customer_id_fkey(id, first_name, last_name, phone, email)
    `)
    .eq('business_id', businessId)
    .eq('mobile_booking', !isLocationBooking)
    .eq('is_deleted', false)
    .or('hidden.is.null,hidden.eq.false')
    .or(`start_date.gte.${formattedStartDate},end_date.lte.${formattedEndDate}`)
    .order('start_date', { ascending: true });

  if (error) {
    console.error('Error fetching basic booking blocks:', error);
    throw error;
  }

  if (!bookings) return [];
  
  // Simplified color mapping (same as full function)
  const getStatusColor = (status: string, booking: any) => {
    // For cancelled bookings, always use pale red regardless of any other colors
    if (status === 'cancelled') {
      return '#fee2e2'; // Pale red (red-100)
    }
    
    // For scheduled bookings, use the booking's color if available
    if (status === 'scheduled' && booking.color) {
      return booking.color;
    }
    
    // For in_progress, always use yellow
    if (status === 'in_progress') {
      return '#eab308'; // Yellow
    }
    
    // Use default status colors for other statuses
    switch (status) {
      case 'pending':
        return '#a855f7'; // Purple
      case 'scheduled':
        return '#3b82f6'; // Blue (default)
      case 'completed':
        return '#22c55e'; // Green
      default:
        return '#3b82f6'; // Default blue
    }
  };

  // Process bookings into basic blocks with immediate visual completion
  return bookings.map(booking => {
    const start = `${booking.start_date}T${booking.start_time}`;
    let end = booking.end_date && booking.end_time 
      ? `${booking.end_date}T${booking.end_time}`
      : start;
    let allDay = false;
    if (booking.start_date !== booking.end_date) {
      const startTime = booking.start_time;
      const endTime = booking.end_time;
      if ((startTime === '00:00' || startTime === '00:00:00') && (endTime === '00:00' || endTime === '00:00:00')) {
        allDay = true;
        end = getExclusiveEnd(start, end, true);
      }
    }
    
    const color = getStatusColor(booking.status, booking);
    
    // Create better display title and customer name
    const customerName = booking.customer 
      ? `${booking.customer.first_name} ${booking.customer.last_name}`
      : 'No Customer';
    
    const displayTitle = booking.title || `Booking #${booking.booking_number}`;
    
    return {
      id: booking.id,
      title: displayTitle,
      booking_number: booking.booking_number,
      start,
      end,
      backgroundColor: color,
      borderColor: color,
      secondaryColor: booking.secondary_color || null,
      allDay,
      display: 'block',
      extendedProps: {
        customer: customerName,
        customer_id: booking.customer?.id || null,
        customer_phone: booking.customer?.phone || null,
        customer_email: booking.customer?.email || null,
        status: booking.status,
        booking_number: booking.booking_number,
        drop_off_time: booking.drop_off_time || null,
        start_time: booking.start_time || null,
        secondary_color: booking.secondary_color || null,
        subtotal: 0,
        total_amount: 0,
        payments: [],
        services: [],
        resources: [],
        is_buffer_block: false,
        mobile_booking: !isLocationBooking,
        customer_notes: ''
      }
    };
  });
}

export interface UnscheduledBookingResource {
  id: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
}

export interface UnscheduledBookingSummary {
  id: string;
  title: string | null;
  booking_number: number | null;
  status: string;
  color: string | null;
  secondary_color: string | null;
  total_amount: number | null;
  subtotal: number | null;
  discount_amount: number | null;
  /** Total service duration (minutes), min 60 — used to auto-fill the schedule picker end time. */
  service_duration: number;
  created_at: string;
  customer_name: string;
  vehicle_labels: string[];
  resources: UnscheduledBookingResource[];
  /** Records linked to this booking, surfaced as chips on the block. */
  links: {
    fieldQuotes: number;
    proposals: number;
    hasProject: boolean;
    hasOpportunity: boolean;
  };
}

/**
 * Bookings created without a date yet ("limbo" state). These have NULL
 * start/end dates so they never render on the calendar grid — they are
 * surfaced through the Unscheduled panel instead.
 */
export async function getUnscheduledBookings(
  businessId: string
): Promise<UnscheduledBookingSummary[]> {
  if (!supabase) throw new Error('Supabase client is not initialized');

  const { data, error } = await supabase
    .from('bookings')
    .select(`
      id,
      title,
      booking_number,
      status,
      color,
      secondary_color,
      total_amount,
      subtotal,
      discount_amount,
      project_id,
      pipeline_opportunities_id,
      created_at,
      customer:customers!bookings_customer_id_fkey(first_name, last_name),
      services:booking_services(
        duration_minutes,
        vehicle:customer_vehicles(make, model, year)
      ),
      resources:booking_resources(
        resource:resources(
          id,
          first_name,
          last_name,
          avatar_url
        )
      )
    `)
    .eq('business_id', businessId)
    .eq('is_unscheduled', true)
    .eq('is_deleted', false)
    .neq('status', 'cancelled')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching unscheduled bookings:', error);
    throw error;
  }

  const bookingIds = (data ?? []).map((row: any) => row.id);

  // Batch-load field quote and proposal links so each block can show what it's tied to.
  const fieldQuoteCounts = new Map<string, number>();
  const proposalCounts = new Map<string, number>();
  if (bookingIds.length) {
    const [{ data: bfqRows }, { data: proposalRows }] = await Promise.all([
      supabase
        .from('booking_field_quotes')
        .select('booking_id')
        .in('booking_id', bookingIds),
      supabase
        .from('proposals')
        .select('booking_id')
        .in('booking_id', bookingIds),
    ]);
    for (const r of bfqRows ?? []) {
      fieldQuoteCounts.set(r.booking_id, (fieldQuoteCounts.get(r.booking_id) ?? 0) + 1);
    }
    for (const r of proposalRows ?? []) {
      if (!r.booking_id) continue;
      proposalCounts.set(r.booking_id, (proposalCounts.get(r.booking_id) ?? 0) + 1);
    }
  }

  return (data ?? []).map((row: any) => {
    const customer = Array.isArray(row.customer) ? row.customer[0] : row.customer;
    const customer_name =
      `${customer?.first_name ?? ''} ${customer?.last_name ?? ''}`.trim() ||
      'No customer';

    const labels = new Set<string>();
    let durationTotal = 0;
    for (const service of row.services ?? []) {
      durationTotal += service?.duration_minutes || 0;
      const v = Array.isArray(service.vehicle) ? service.vehicle[0] : service.vehicle;
      if (!v) continue;
      const parts = [v.year, v.make, v.model].filter(Boolean);
      if (parts.length) labels.add(parts.join(' '));
    }

    const resources: UnscheduledBookingResource[] = [];
    for (const link of row.resources ?? []) {
      const r = Array.isArray(link.resource) ? link.resource[0] : link.resource;
      if (!r) continue;
      resources.push({
        id: r.id,
        first_name: r.first_name ?? null,
        last_name: r.last_name ?? null,
        avatar_url: r.avatar_url ?? null,
      });
    }

    return {
      id: row.id,
      title: row.title ?? null,
      booking_number: row.booking_number ?? null,
      status: row.status,
      color: row.color ?? null,
      secondary_color: row.secondary_color ?? null,
      total_amount: row.total_amount ?? null,
      subtotal: row.subtotal ?? null,
      discount_amount: row.discount_amount ?? null,
      service_duration: Math.max(60, durationTotal),
      created_at: row.created_at,
      customer_name,
      vehicle_labels: [...labels],
      resources,
      links: {
        fieldQuotes: fieldQuoteCounts.get(row.id) ?? 0,
        proposals: proposalCounts.get(row.id) ?? 0,
        hasProject: !!row.project_id,
        hasOpportunity: row.pipeline_opportunities_id != null,
      },
    };
  });
}

// ---------------------------------------------------------------------------
// Booking <-> Field Quote linking (booking_field_quotes join table)
// ---------------------------------------------------------------------------

export interface LinkedFieldQuoteResource {
  id: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
}

export interface LinkedFieldQuote {
  id: string;
  status: string;
  is_measurement: boolean;
  start_date: string | null;
  start_time: string | null;
  end_date: string | null;
  end_time: string | null;
  field_quote_address: string | null;
  technician_notes: string | null;
  project_id: string | null;
  project_title: string | null;
  created_at: string | null;
  updated_at: string | null;
  resources: LinkedFieldQuoteResource[];
}

export interface FieldQuoteCandidate extends LinkedFieldQuote {
  /** How this candidate was matched to the booking context. */
  matched_by: 'proposal' | 'opportunity' | 'project' | 'customer';
}

function mapFieldQuoteRow(row: any): LinkedFieldQuote {
  const project = Array.isArray(row.project) ? row.project[0] : row.project;

  // Combine the primary assigned resource with any junction-table resources,
  // de-duplicated by id.
  const resources: LinkedFieldQuoteResource[] = [];
  const seenResourceIds = new Set<string>();
  const pushResource = (r: any) => {
    if (!r?.id || seenResourceIds.has(r.id)) return;
    seenResourceIds.add(r.id);
    resources.push({
      id: r.id,
      first_name: r.first_name ?? null,
      last_name: r.last_name ?? null,
      avatar_url: r.avatar_url ?? null,
    });
  };
  const primary = Array.isArray(row.resource) ? row.resource[0] : row.resource;
  if (primary) pushResource(primary);
  for (const link of row.project_field_quote_resources ?? []) {
    const r = Array.isArray(link?.resource) ? link.resource[0] : link?.resource;
    if (r) pushResource(r);
  }

  return {
    id: row.id,
    status: row.status,
    is_measurement: !!row.is_measurement,
    start_date: row.start_date ?? null,
    start_time: row.start_time ?? null,
    end_date: row.end_date ?? null,
    end_time: row.end_time ?? null,
    field_quote_address: row.field_quote_address ?? null,
    technician_notes: row.technician_notes ?? null,
    project_id: row.project_id ?? null,
    project_title: project?.title ?? null,
    created_at: row.created_at ?? null,
    updated_at: row.updated_at ?? null,
    resources,
  };
}

const FIELD_QUOTE_SELECT = `
  id,
  status,
  is_measurement,
  start_date,
  start_time,
  end_date,
  end_time,
  field_quote_address,
  technician_notes,
  project_id,
  created_at,
  updated_at,
  project:projects(title),
  resource:resources!project_field_quotes_resource_id_fkey(id, first_name, last_name, avatar_url),
  project_field_quote_resources(
    resource:resources(id, first_name, last_name, avatar_url)
  )
`;

/**
 * Link one or more field quotes to a booking. Idempotent — existing links are
 * left untouched (UNIQUE(booking_id, field_quote_id)). Never throws so it can't
 * break the surrounding booking creation flow.
 */
/**
 * If a booking has no opportunity yet, auto-link it to the customer's most recent
 * open CRM opportunity — mirrors proposal/field-quote behavior — and record the
 * activity so the opportunity is marked read. Never throws.
 */
async function autoLinkBookingToOpenOpportunity(
  bookingId: string,
  businessId: string,
  customerId: string,
): Promise<void> {
  try {
    const linkable = await findOpenLinkableOpportunityForCustomer(businessId, customerId);
    if (!linkable) return;

    const { error } = await client
      .from('bookings')
      .update({ pipeline_opportunities_id: linkable.id })
      .eq('id', bookingId);

    if (error) {
      console.error('Error auto-linking booking to opportunity:', error);
      return;
    }

    recordOpportunityActivity({
      businessId,
      customerId,
      activityType: 'booking_linked',
      activityData: { booking_id: bookingId },
      specificOpportunityId: linkable.id,
    }).catch((err) => console.error('Error recording booking_linked activity:', err));
  } catch (err) {
    console.error('Error during booking opportunity auto-link:', err);
  }
}

export async function linkFieldQuotesToBooking(
  bookingId: string,
  fieldQuoteIds: string[],
  businessId: string
): Promise<void> {
  if (!supabase || !bookingId || !businessId || !fieldQuoteIds?.length) return;

  const rows = [...new Set(fieldQuoteIds.filter(Boolean))].map((field_quote_id) => ({
    booking_id: bookingId,
    field_quote_id,
    business_id: businessId,
  }));
  if (rows.length === 0) return;

  const { error } = await supabase
    .from('booking_field_quotes')
    .upsert(rows, { onConflict: 'booking_id,field_quote_id', ignoreDuplicates: true });

  if (error) {
    console.error('Error linking field quotes to booking:', error);
  }
}

export async function unlinkFieldQuoteFromBooking(
  bookingId: string,
  fieldQuoteId: string
): Promise<void> {
  if (!supabase) throw new Error('Supabase client is not initialized');

  const { error } = await supabase
    .from('booking_field_quotes')
    .delete()
    .eq('booking_id', bookingId)
    .eq('field_quote_id', fieldQuoteId);

  if (error) {
    console.error('Error unlinking field quote from booking:', error);
    throw error;
  }
}

/** Field quotes currently linked to a booking. */
export async function getFieldQuotesForBooking(bookingId: string): Promise<LinkedFieldQuote[]> {
  if (!supabase || !bookingId) return [];

  const { data, error } = await supabase
    .from('booking_field_quotes')
    .select(`field_quote:project_field_quotes(${FIELD_QUOTE_SELECT})`)
    .eq('booking_id', bookingId);

  if (error) {
    console.error('Error loading field quotes for booking:', error);
    return [];
  }

  return (data ?? [])
    .map((row: any) => (Array.isArray(row.field_quote) ? row.field_quote[0] : row.field_quote))
    .filter(Boolean)
    .map(mapFieldQuoteRow);
}

/** Booking ids currently linked to a field quote. */
export async function getBookingIdsForFieldQuote(fieldQuoteId: string): Promise<string[]> {
  if (!supabase || !fieldQuoteId) return [];

  const { data, error } = await supabase
    .from('booking_field_quotes')
    .select('booking_id')
    .eq('field_quote_id', fieldQuoteId);

  if (error) {
    console.error('Error loading bookings for field quote:', error);
    return [];
  }
  return (data ?? []).map((r: any) => r.booking_id);
}

/**
 * Find candidate field quotes to associate with a booking being created, in
 * priority order: proposal -> opportunity -> project -> customer. Measurements
 * are excluded (only real field quotes count as a booking origin). Deduped by id.
 */
export async function resolveCandidateFieldQuotes(opts: {
  businessId: string;
  proposalId?: string | null;
  opportunityId?: number | null;
  projectId?: string | null;
  customerId?: string | null;
}): Promise<FieldQuoteCandidate[]> {
  if (!supabase || !opts.businessId) return [];

  const seen = new Set<string>();
  const out: FieldQuoteCandidate[] = [];

  const pushRows = (rows: any[], matched_by: FieldQuoteCandidate['matched_by']) => {
    for (const row of rows ?? []) {
      if (seen.has(row.id)) continue;
      seen.add(row.id);
      out.push({ ...mapFieldQuoteRow(row), matched_by });
    }
  };

  // 1) Field quote directly attached to the proposal being converted
  if (opts.proposalId) {
    const { data: proposal } = await supabase
      .from('proposals')
      .select('field_quote_id')
      .eq('id', opts.proposalId)
      .maybeSingle();

    if (proposal?.field_quote_id) {
      const { data } = await supabase
        .from('project_field_quotes')
        .select(FIELD_QUOTE_SELECT)
        .eq('id', proposal.field_quote_id)
        .eq('is_measurement', false);
      pushRows(data ?? [], 'proposal');
    }
  }

  // 2) Same CRM opportunity
  if (opts.opportunityId != null) {
    const { data } = await supabase
      .from('project_field_quotes')
      .select(FIELD_QUOTE_SELECT)
      .eq('business_id', opts.businessId)
      .eq('is_measurement', false)
      .eq('pipeline_opportunities_id', opts.opportunityId)
      .order('start_date', { ascending: false, nullsFirst: false });
    pushRows(data ?? [], 'opportunity');
  }

  // 3) Same project
  if (opts.projectId) {
    const { data } = await supabase
      .from('project_field_quotes')
      .select(FIELD_QUOTE_SELECT)
      .eq('business_id', opts.businessId)
      .eq('is_measurement', false)
      .eq('project_id', opts.projectId)
      .order('start_date', { ascending: false, nullsFirst: false });
    pushRows(data ?? [], 'project');
  }

  // 4) Same customer (lowest confidence)
  if (opts.customerId) {
    const { data } = await supabase
      .from('project_field_quotes')
      .select(FIELD_QUOTE_SELECT)
      .eq('business_id', opts.businessId)
      .eq('is_measurement', false)
      .eq('customer_id', opts.customerId)
      .order('start_date', { ascending: false, nullsFirst: false })
      .limit(10);
    pushRows(data ?? [], 'customer');
  }

  return out;
}